import type { CursorPaginatedResponse, PackageDeal, PackageDealListParams } from '@/libs/interfaces'

import { mockPackageDeals } from '../fixtures/packageDeals'
import { delay, genID, matchesSearch, nowISO, paginate } from '../helpers'
import { createEntityStore } from '../store'

const packageDealStore = createEntityStore<PackageDeal, 'packageID'>(mockPackageDeals, 'packageID')

const filterPackageDeals = (params: PackageDealListParams): PackageDeal[] =>
  packageDealStore
    .find(
      (packageDeal) => packageDeal.orgID === params.orgID && packageDeal.storeID === params.storeID,
    )
    .filter((packageDeal) =>
      params.packageStatus ? packageDeal.packageStatus === params.packageStatus : true,
    )
    .filter((packageDeal) => matchesSearch(packageDeal, params.search, ['name', 'description']))

export const packageDealsApiMock = {
  listCursor: async (
    params: PackageDealListParams,
  ): Promise<CursorPaginatedResponse<PackageDeal>> =>
    delay(paginate(filterPackageDeals(params), params)),

  list: async (params: PackageDealListParams): Promise<PackageDeal[]> =>
    delay(filterPackageDeals(params)),

  getByID: async (params: {
    orgID: string
    storeID: string
    packageID: string
  }): Promise<Partial<PackageDeal>> => delay(packageDealStore.getById(params.packageID) ?? {}),

  create: async (body: Partial<PackageDeal>): Promise<Partial<PackageDeal>> => {
    const packageDeal: PackageDeal = {
      packageID: genID('package'),
      packageStatus: 'Active',
      packageVariants: [],
      ...body,
      createdAt: nowISO(),
      updatedAt: nowISO(),
    }
    packageDealStore.insert(packageDeal)
    return delay(packageDeal)
  },

  update: async (payload: Partial<PackageDeal>): Promise<Partial<PackageDeal>> => {
    const updated = payload.packageID
      ? packageDealStore.update(payload.packageID, {
          ...payload,
          updatedAt: nowISO(),
        })
      : undefined
    return delay(updated ?? {})
  },

  delete: async (payload: Partial<PackageDeal>): Promise<Partial<PackageDeal>> => {
    const packageDeal = payload.packageID ? packageDealStore.getById(payload.packageID) : undefined
    if (payload.packageID) packageDealStore.remove(payload.packageID)
    return delay(packageDeal ?? {})
  },
}
